import { NavLink, useLocation } from 'react-router-dom'

import { paths } from 'src/config/paths'
import BorderedProfilePhoto from 'src/features/notifications/components/ui/bordered-profile-photo'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import { professionMap } from 'src/shared/features/users/schemas/profession'
import { cn } from 'src/shared/utils/cn'

type SidebarRouteItem = {
  label: string
  to: string
}

type SidebarSection = {
  title: string
  items: SidebarRouteItem[]
}

const settingsSections: SidebarSection[] = [
  {
    title: 'Account',
    items: [
      { label: 'Personal information', to: paths.settings.personalInfo.getHref() },
      { label: 'Profile', to: '/settings/profile' },
      { label: 'Phone number', to: '/settings/conrad-phone-number' },
      { label: 'Preferences', to: '/settings/account-preferences' },
      { label: 'Company', to: '/settings/company' },
      { label: 'Membership', to: paths.settings.subscriptions.getHref() },
      { label: 'Integrations', to: paths.settings.integrations.getHref() },
      { label: 'Notifications', to: paths.settings.notifications.getHref() },
    ],
  },
  {
    title: 'Support',
    items: [
      { label: 'Login & security', to: paths.settings.security.getHref() },
      { label: 'Invite network', to: '/settings/invite-network' },
      { label: 'About Conrad Pro', to: '/settings/about-conrad-pro' },
    ],
  },
  {
    title: 'Legal',
    items: [
      { label: 'Terms of use', to: '/settings/terms-of-use' },
      { label: 'Privacy policy', to: '/settings/privacy-policy' },
    ],
  },
]

const SidebarNav = () => {
  const { data: user } = useMyUserData()
  const location = useLocation()

  const activePath = location.pathname
  const isPersonalInfoActive = activePath === paths.settings.path

  return (
    <div className="flex flex-col gap-7">
      <h1 className="text-[26px] font-semibold leading-none text-primary">Settings</h1>

      <div className="flex items-center gap-3">
        <BorderedProfilePhoto uri={user?.profilePhoto || null} size={48} />
        <div className="min-w-0">
          <div className="flex items-center gap-1">
            <p className="truncate text-sm font-semibold tracking-[-0.28px] text-primary">{user?.fullName || '-'}</p>
            <div className="truncate rounded-full bg-accent px-2.5 py-1 text-[11px] font-medium tracking-[-0.28px] text-primary">
              <p>{professionMap[user?.profession as Profession]?.label || ''}</p>
            </div>
          </div>
          <p className="truncate text-xs text-muted">@{user?.username || '---'}</p>
        </div>
      </div>

      <div className="flex flex-col gap-5">
        {settingsSections.map((section) => (
          <div key={section.title} className="flex flex-col gap-2.5">
            <p className="text-sm font-semibold text-muted">{section.title}</p>
            <div className="flex flex-col gap-1">
              {section.items.map((item) => {
                const shouldHideCompanyTab =
                  section.title === 'Account' && item.label === 'Company' && user?.enterpriseId == null

                if (shouldHideCompanyTab) return null

                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        'rounded-[12px] px-3.5 py-3 text-sm font-semibold text-primary transition-colors',
                        (isActive || (isPersonalInfoActive && item.to === paths.settings.personalInfo.getHref())) &&
                          'bg-accent',
                        !isActive &&
                          !(isPersonalInfoActive && item.to === paths.settings.personalInfo.getHref()) &&
                          'hover:bg-accent/60',
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default SidebarNav
